import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useDispatch } from 'react-redux';
import { setToolbarTitle } from '../../../store/to-native';
import { showFullScreenError } from '../../../store/actions/error';
import LibraryFeaturedContent from '../../../components/library-featured-content';
import ScrollingSection from '../../../components/scrolling-section';

import { generateCategoryRoute } from '../../../helpers';
import { FREE_CATEGORY_TITLE } from '../../../helpers/merge-slugs';
import { NotFound } from '../../../helpers/not-found';
import {
  subcategoriesTitle,
  freeCategoriesTitle,
  seriesTitle,
} from '../../../i18n/translations';
import { getSearchState } from '../../search/state/search-state';
import { CategoryParams } from '../../routes';
import { useSeriesRowByCategoryQuery } from '../../../client/query/series';
import { useFeaturedContentCategoryQuery } from '../../../client/query/featured-content';
import { useGetFilterOptions } from '../../../client/query/filter-options';
import { useGetCategoryRowsQuery } from '../../../client/query/entities';

const Category: React.FC = () => {
  const { data: filterOptions } = useGetFilterOptions();
  const { categorySlug }: CategoryParams = useParams();
  const decodedCategorySlug = decodeURIComponent(categorySlug);
  const isFreeCategory = categorySlug === FREE_CATEGORY_TITLE;
  const dispatch = useDispatch();
  const [allSectionsErrored, setAllSectionsErrored] = useState(false);
  useEffect(() => {
    const category = filterOptions?.categoryOptions?.find(
      cat => cat.value === decodedCategorySlug,
    );
    setToolbarTitle(category?.display);
    let filters = null;
    if (decodedCategorySlug === 'International') {
      filters = { languages: [] };
    } else if (decodedCategorySlug === 'Included Workouts') {
      filters = { categories: [] };
    } else {
      filters = {
        categories: [categorySlug],
      };
    }
    getSearchState().setViewContext(filters);
  }, [decodedCategorySlug, categorySlug, filterOptions]);

  const params = isFreeCategory
    ? { freeOnly: true }
    : { category: categorySlug };

  const {
    isError: isCategoriesError,
    data: categories,
    isLoading: isCategoriesLoading,
  } = useGetCategoryRowsQuery(params);

  const {
    isError: isSeriesContentError,
    data: seriesContent,
  } = useSeriesRowByCategoryQuery();

  const {
    isError: isFeaturedContentError,
    data: featuredContent,
  } = useFeaturedContentCategoryQuery();

  useEffect(() => {
    if (allSectionsErrored) {
      dispatch(showFullScreenError());
    }
  }, [allSectionsErrored, dispatch]);
  useEffect(() => {
    setAllSectionsErrored(
      isCategoriesError && isSeriesContentError && isFeaturedContentError,
    );
  }, [isCategoriesError, isSeriesContentError, isFeaturedContentError]);

  if (
    featuredContent?.featuredItems.length === 0 &&
    seriesContent?.data.length === 0 &&
    !isCategoriesLoading &&
    categories?.length === 0
  ) {
    return <NotFound />;
  }
  if (allSectionsErrored) {
    return null;
  }
  const featuredItems = featuredContent?.featuredItems;
  return (
    <div className="category-view" data-testid={'category-view'}>
      {!isFreeCategory && (
        <LibraryFeaturedContent featuredItems={featuredItems} />
      )}
      {!isFreeCategory && (
        <ScrollingSection
          title={seriesTitle()}
          listData={seriesContent}
          route={`/programs/${encodeURIComponent(categorySlug)}`}
          large
        />
      )}
      {!categories?.length && isCategoriesLoading && (
        <ScrollingSection
          key={'Subcategories Placeholder'}
          title={isFreeCategory ? freeCategoriesTitle() : subcategoriesTitle()}
          listData={undefined}
        />
      )}
      {!isCategoriesError &&
        categories?.map(subcategory => {
          return (
            <ScrollingSection
              key={`${decodedCategorySlug}-${subcategory.title}`}
              title={subcategory.title}
              translatedTitle={subcategory.translatedTitle}
              listData={{
                hasMoreItems: subcategory.hasMoreItems,
                data: subcategory.data,
              }}
              route={generateCategoryRoute(
                decodedCategorySlug,
                subcategory.title,
              )}
            />
          );
        })}
    </div>
  );
};

export default Category;
